import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ShortTextRoundedIcon from '@mui/icons-material/ShortTextRounded';
import { resolveCopySelectionShortcuts } from './chatMessageCopyUtils';
import '../../../../css/common/chat/ChatMessageSelectableCopyArea.css';

const isSelectionInsideRoot = (selection, root) => {
  if (!selection || !root) return false;
  if (selection.rangeCount < 1 || selection.isCollapsed) return false;
  const { anchorNode, focusNode } = selection;
  return Boolean(anchorNode && focusNode && root.contains(anchorNode) && root.contains(focusNode));
};

const collectTextSegments = (root) => {
  if (!root || typeof document === 'undefined') return [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const segments = [];
  let cursor = 0;
  let node = walker.nextNode();
  while (node) {
    const value = String(node.nodeValue || '');
    if (value) {
      segments.push({
        node,
        start: cursor,
        end: cursor + value.length,
      });
      cursor += value.length;
    }
    node = walker.nextNode();
  }
  return segments;
};

const applySelectionRange = (root, start, end) => {
  if (!root || typeof window === 'undefined' || typeof document === 'undefined') return '';
  const segments = collectTextSegments(root);
  const startSegment = segments.find((segment) => start >= segment.start && start < segment.end);
  const endSegment = segments.find((segment) => end > segment.start && end <= segment.end);
  if (!startSegment || !endSegment) return '';

  const range = document.createRange();
  range.setStart(startSegment.node, Math.max(0, start - startSegment.start));
  range.setEnd(endSegment.node, Math.max(0, end - endSegment.start));
  const selection = window.getSelection?.();
  selection?.removeAllRanges?.();
  selection?.addRange?.(range);
  return String(selection?.toString?.() || '').trim();
};

const SHORTCUT_BUTTONS = [
  { key: 'link', Icon: LinkRoundedIcon, label: 'URL を自動選択' },
  { key: 'address', Icon: PlaceRoundedIcon, label: '住所を自動選択' },
  { key: 'phone', Icon: CallRoundedIcon, label: '電話番号を自動選択' },
  { key: 'name', Icon: PersonRoundedIcon, label: '名前を自動選択' },
  { key: 'word', Icon: ShortTextRoundedIcon, label: '単語を自動選択' },
];

export default function ChatMessageSelectableCopyArea({
  active = false,
  messageId = '',
  copyButtonSide = 'right',
  onCopySelection,
  onDeactivate,
  children = null,
}) {
  const rootRef = useRef(null);
  const [selectedText, setSelectedText] = useState('');
  const [shortcutCursorMap, setShortcutCursorMap] = useState({});
  const [sourceText, setSourceText] = useState('');

  const clearBrowserSelection = useCallback(() => {
    if (typeof window === 'undefined') return;
    const selection = window.getSelection?.();
    selection?.removeAllRanges?.();
  }, []);

  const syncSelection = useCallback(() => {
    if (typeof window === 'undefined') return;
    const selection = window.getSelection?.();
    if (!isSelectionInsideRoot(selection, rootRef.current)) {
      setSelectedText('');
      return;
    }
    const nextText = String(selection?.toString?.() || '').trim();
    setSelectedText(nextText);
  }, []);

  useEffect(() => {
    if (!active) {
      setSourceText('');
      return;
    }
    setSourceText(String(rootRef.current?.textContent || ''));
  }, [active, children, messageId]);

  const shortcutGroups = useMemo(() => resolveCopySelectionShortcuts(sourceText), [sourceText]);

  useEffect(() => {
    if (!active) {
      setSelectedText('');
      setShortcutCursorMap({});
      setSourceText('');
      clearBrowserSelection();
      return undefined;
    }

    const handleSelectionChange = () => {
      syncSelection();
    };

    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      setSelectedText('');
      setShortcutCursorMap({});
      clearBrowserSelection();
      onDeactivate?.();
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setSelectedText('');
      setShortcutCursorMap({});
      clearBrowserSelection();
      onDeactivate?.();
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [active, clearBrowserSelection, onDeactivate, syncSelection]);

  const handleCopy = useCallback(async () => {
    if (!selectedText) return;
    await onCopySelection?.(selectedText);
    setSelectedText('');
    setShortcutCursorMap({});
    clearBrowserSelection();
    onDeactivate?.();
  }, [clearBrowserSelection, onCopySelection, onDeactivate, selectedText]);

  const handleShortcutSelect = useCallback((shortcutKey) => {
    const group = shortcutGroups.find((item) => item.key === shortcutKey);
    if (!group || !Array.isArray(group.matches) || group.matches.length < 1) return;
    const currentIndex = Number(shortcutCursorMap[shortcutKey] || 0);
    const nextIndex = currentIndex % group.matches.length;
    const target = group.matches[nextIndex];
    const nextText = applySelectionRange(rootRef.current, target.start, target.end);
    setSelectedText(nextText);
    setShortcutCursorMap((current) => ({
      ...current,
      [shortcutKey]: nextIndex + 1,
    }));
  }, [shortcutCursorMap, shortcutGroups]);

  return (
    <div
      ref={rootRef}
      data-chat-copy-root={messageId || undefined}
      className={`chat-selectable-copy-area chat-selectable-copy-area--${copyButtonSide}${active ? ' is-copy-mode' : ''}`}
    >
      {children}
      {active ? (
        <div className={`chat-selectable-copy-area__controls chat-selectable-copy-area__controls--${copyButtonSide}`}>
          <div className="chat-selectable-copy-area__shortcutRow">
            {SHORTCUT_BUTTONS.map(({ key, Icon, label }) => {
              const group = shortcutGroups.find((item) => item.key === key);
              const disabled = !group || group.matches.length < 1;
              return (
                <button
                  key={key}
                  type="button"
                  className="chat-selectable-copy-area__shortcutButton"
                  onClick={() => handleShortcutSelect(key)}
                  disabled={disabled}
                  aria-label={label}
                  title={label}
                >
                  <Icon fontSize="inherit" />
                </button>
              );
            })}
          </div>
          {selectedText ? (
            <button
              type="button"
              className="chat-selectable-copy-area__copyButton"
              onClick={handleCopy}
            >
              <ContentCopyRoundedIcon fontSize="inherit" />
              <span>コピー</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
